"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const material_1 = require("@mui/material");
const react_query_1 = require("@tanstack/react-query");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const lodash_1 = __importDefault(require("lodash"));
const pino_1 = __importDefault(require("pino"));
const react_1 = require("react");
const appConstants_1 = require("../../../../constants/appConstants");
const strings_1 = require("../../../../constants/strings");
const uiConstants_1 = require("../../../../constants/uiConstants");
const FormLabel_1 = require("../../../../material-ui-theme/components/styled/FormLabel");
const StyledMuiTelInput_1 = require("../../../../material-ui-theme/components/styled/StyledMuiTelInput");
const companyQuery_1 = require("../../../../queries/companyQuery");
const paymentQuery_1 = require("../../../../queries/paymentQuery");
const rolesQuery_1 = require("../../../../queries/rolesQuery");
const teamQuery_1 = require("../../../../queries/teamQuery");
const userQuery_1 = require("../../../../queries/userQuery");
const paymentRepository_1 = require("../../../../repositories/paymentRepository");
const teamRepository_1 = require("../../../../repositories/teamRepository");
const walletRepository_1 = require("../../../../repositories/walletRepository");
const avatarUtils_1 = require("../../../../utils/avatarUtils");
const zustandStore_1 = require("../../../../zustandStore");
const simpleButton_1 = __importDefault(require("../../../atoms/button/simpleButton"));
const drawerAside_1 = __importDefault(require("../../drawerAside"));
const addPeopleAside_1 = require("../addPeople/addPeopleAside");
const peopleTable_1 = require("../peopleTable");
const requestLimitInput_1 = __importDefault(require("./requestLimitInput"));
const lexico = new lexico_1.default();
const propertyKey = appConstants_1.teamFeaturesConstants.requestLimit;
const logger = (0, pino_1.default)();
const isActive = function (card) {
    if (!card) {
        return false;
    }
    const DAY_IN_MILLISECONDS = 1000 * 60 * 60 * 24;
    const endOfValidityPeriod = card?.validEndingOn;
    if (!endOfValidityPeriod) {
        return false;
    }
    const timeRemaining = new Date(endOfValidityPeriod).getTime() - Date.now();
    const daysRemaining = Math.floor(timeRemaining / DAY_IN_MILLISECONDS);
    const isExpired = daysRemaining < 0;
    const isCanceled = card?.status === 'CANCELED' ||
        card?.status === 'UNAVAILABLE' ||
        card?.status === 'CREATION_ERROR' ||
        card?.status === 'UNKNOWN' ||
        (!isExpired && card?.status === 'EXPIRED');
    return !isExpired && !isCanceled;
};
const fetchCardInfo = async (activeCardUsers) => {
    try {
        if (!activeCardUsers?.tasks) {
            logger.warn('No active card users or tasks found');
            return false;
        }
        const tasksWithCards = activeCardUsers.tasks.filter(task => Boolean(task?.card));
        const results = await Promise.all(tasksWithCards.map(async (task) => {
            try {
                const cardInfo = await paymentRepository_1.PaymentRepository.getVirtualCardInfo({
                    documentId: task?.request?.requestId || '',
                });
                const updatedCard = { ...task?.card, ...cardInfo };
                return isActive(updatedCard);
            }
            catch (error) {
                logger.error({ message: 'Error fetching card info for task', task, error });
                return false;
            }
        }));
        return results.some(Boolean);
    }
    catch (error) {
        logger.error({ message: 'Error in fetchCardInfo', error });
        return false;
    }
};
const UserDetailsAside = () => {
    const setUserTeamProperty = (0, teamQuery_1.useSetUserTeamPropertyQuery)();
    const { data: teamId } = (0, react_query_1.useQuery)((0, teamQuery_1.selectedTeamIdQuery)('selectedTeamID'));
    const [hasRequestLimit, setHasRequestLimit] = (0, react_1.useState)(true);
    const isUserDetailsAsideOpen = (0, zustandStore_1.useZustand)(state => state.aside.userDetails.isOpen);
    const currentUserDetailsAside = (0, zustandStore_1.useZustand)(state => state.aside.userDetails.user);
    const isUserDetailSaveBtnDisabled = (0, zustandStore_1.useZustand)(state => state.aside.userDetails.isSaveDisabled);
    const { setPageLoading } = (0, zustandStore_1.useZustand)();
    const reqLimitError = (0, zustandStore_1.useZustand)(state => state.aside.userDetails.reqLimitError);
    const isOooAvailable = false; // ! this is always false why ?
    const [approver, setApprover] = (0, react_1.useState)({});
    const [requestLimit, setRequestLimit] = (0, react_1.useState)('');
    const roles = (0, zustandStore_1.useZustand)(state => state.role.roles);
    const teamID = teamId && teamId.length > 0 ? teamId?.[0] || '' : '';
    const { refetch: refetchGetTeamApprovers } = (0, paymentQuery_1.useGetTeamApproversQuery)({
        excludeSelf: false,
        teamId: teamID,
    });
    const removeUserFromTeam = (0, teamQuery_1.useRemoveUserFromTeamQuery)();
    const { setCompanyProperty, setIsUserDetailsAsideOpen, setIsUserDetailSaveBtnDisabled, setRequestLimitError, setNotification, setDialog, } = (0, zustandStore_1.useZustand)();
    const { data: curentUser } = (0, react_query_1.useQuery)(userQuery_1.userQuery);
    const { data: companyPropertiesData } = (0, react_query_1.useQuery)((0, companyQuery_1.companyPropertiesQuery)(curentUser?.companyAccountId, {
        cardprovider: 'tsgo-cardprovider',
    }));
    const cardProvider = companyPropertiesData?.cardprovider;
    const isHsbcCardProvider = cardProvider === 'HSBC';
    const isAmexCardProvider = cardProvider === 'AMEX';
    const updateUserRole = (0, rolesQuery_1.useUpdateUserRoleQuery)();
    const user = currentUserDetailsAside;
    const currentTeamApprovers = (0, zustandStore_1.useZustand)(state => state.payment.approvers);
    const teamApprovers = isAmexCardProvider
        ? currentTeamApprovers
        : currentTeamApprovers?.filter(approver => approver?.userId !== user?.userId) || [];
    const currentTeamRole = (0, peopleTable_1.getUserRoleIdByTeamId)(user?.roles, teamID);
    const currentUserRole = Object.values(roles).find(role => {
        return role && role.id ? role.id === currentTeamRole : false;
    });
    const { data: usersData } = (0, teamQuery_1.useGetTeamUsersQuery)({ teamId: teamID });
    const [selectedRole, setSelectedRole] = (0, react_1.useState)(currentUserRole);
    const { data: requestLimitData } = (0, companyQuery_1.useGetRequestLimitQuery)();
    (0, react_1.useEffect)(() => {
        if (requestLimitData) {
            setCompanyProperty({
                key: propertyKey,
                value: lodash_1.default.isObject(requestLimitData) ? '' : lodash_1.default.toNumber(requestLimitData),
            });
        }
    }, [requestLimitData, setCompanyProperty]);
    (0, react_1.useEffect)(() => {
        void refetchGetTeamApprovers();
    }, []);
    (0, react_1.useEffect)(() => {
        if (currentUserDetailsAside) {
            const role = lodash_1.default.find(currentUserDetailsAside.roles, { securityGroupId: teamID });
            setSelectedRole(role ? { id: role.roleId } : {});
            setApprover(lodash_1.default.get(currentUserDetailsAside, `teams[${teamId}].approver`));
        }
    }, [currentUserDetailsAside, teamID, teamId]);
    (0, react_1.useEffect)(() => {
        setSelectedRole(currentUserRole);
    }, [currentUserRole]);
    (0, react_1.useEffect)(() => {
        setHasRequestLimit(selectedRole?.id === 'manager' || selectedRole?.id === 'team_admin');
    }, [selectedRole]);
    (0, react_1.useEffect)(() => {
        if (hasRequestLimit) {
            setRequestLimit(lodash_1.default.get(currentUserDetailsAside, `teams[${teamId}].requestLimit`) || '');
        }
    }, [hasRequestLimit, currentUserDetailsAside, teamId]);
    const onFormSubmit = ({ teamUsers }) => {
        if (approver) {
            setUserTeamProperty.mutate({
                userId: user?.userId,
                teamId: teamID,
                propertyKey: appConstants_1.userTeamProperties.all.defaultApprover,
                propertyValue: approver?.userId || '',
            });
        }
        if (requestLimit != null) {
            setUserTeamProperty.mutate({
                userId: user?.userId,
                teamId: teamID,
                propertyKey: peopleTable_1.REQUEST_LIMIT_PROPERTY,
                propertyValue: requestLimit,
            });
        }
        if (teamUsers.users.length === 1 && selectedRole.id === 'team_member') {
            setNotification([
                {
                    type: strings_1.notificationStrings.WARNING,
                    message: strings_1.peopleTableStrings.ONLY_USER_WARNING_MESSAGE(user.fullName),
                },
            ]);
            setIsUserDetailsAsideOpen(false);
            return;
        }
        else if ((currentTeamRole === 'team_admin' || currentTeamRole === 'manager') &&
            user.requesters &&
            user.requesters.length > 0 &&
            selectedRole?.id === 'team_member') {
            setNotification([
                {
                    type: strings_1.notificationStrings.WARNING,
                    message: strings_1.peopleTableStrings.APPROVER_WARNING_MESSAGE(user.fullName),
                },
            ]);
            return;
        }
        if (!lodash_1.default.isEmpty(user?.roles) && selectedRole.id) {
            updateUserRole.mutate({ userId: user?.userId, selectedRoleId: selectedRole.id, teamId: teamID });
        }
        setIsUserDetailsAsideOpen(false);
    };
    const changeUserData = e => {
        const { name, value } = e.target;
        if (name === 'roleId') {
            const newSelectedRole = Object.values(roles).find((role) => {
                return role && role.id ? role.id === value : false;
            });
            if (!newSelectedRole) {
                return;
            }
            setSelectedRole(newSelectedRole);
            if (newSelectedRole?.id === 'team_member') {
                setRequestLimitError('');
            }
            setIsUserDetailSaveBtnDisabled(newSelectedRole?.id === 'team_member' ? false : !!(requestLimit && reqLimitError));
        }
    };
    const changeApproverData = e => {
        const { name, value } = e.target;
        setApprover(prevState => ({
            ...prevState,
            [name]: value,
        }));
        setIsUserDetailSaveBtnDisabled(!!reqLimitError);
    };
    const handleAlert = () => {
        const userId = user?.userId;
        const hsbcErrorMessage = [
            { message: isHsbcCardProvider ? removeUserHSBCWarning() : strings_1.userDetailsStrings.NO_TEAM_OWNER_AND_MANAGER_ERROR },
        ];
        setIsUserDetailsAsideOpen(false);
        removeUserFromTeam.mutate({ teamId: teamID, userId }, {
            onError: () => {
                return isHsbcCardProvider
                    ? setNotification([
                        {
                            type: strings_1.notificationStrings.ERROR,
                            message: strings_1.peopleTableStrings.USER_REMOVE_ERROR_MESSAGE,
                        },
                    ])
                    : setDialog({
                        type: strings_1.notificationStrings.WARNING,
                        message: hsbcErrorMessage[0]?.message,
                        open: true,
                        actionText: 'OK',
                        showCancel: false,
                        onAccept: () => ({}),
                    });
            },
            onSuccess: () => {
                setNotification([
                    {
                        type: strings_1.notificationStrings.SUCCESS,
                        message: strings_1.peopleTableStrings.SUCCESS_MESSAGE({ fname: user?.firstName }),
                    },
                ]);
            },
        });
    };
    const removeUserHSBCWarning = () => {
        return ((0, jsx_runtime_1.jsxs)("p", { children: [(0, jsx_runtime_1.jsx)("span", { children: strings_1.userDetailsStrings.HSBC_NOT_REMOVE_USER_ERROR_START }), (0, jsx_runtime_1.jsx)("ul", { style: { display: 'grid', justifyContent: 'space-evenly' }, children: strings_1.userDetailsStrings.HSBC_NOT_REMOVE_USER_ERROR_BULLETS.map((item, i) => ((0, jsx_runtime_1.jsx)("li", { children: item }, i))) }), (0, jsx_runtime_1.jsx)("span", { children: strings_1.userDetailsStrings.HSBC_NOT_REMOVE_USER_ERROR_END })] }));
    };
    const removeUser = async () => {
        try {
            setPageLoading(true);
            if (!user || !teamID) {
                setPageLoading(false);
                setNotification([
                    {
                        type: strings_1.notificationStrings.ERROR,
                        message: strings_1.peopleTableStrings.USER_REMOVE_ERROR_MESSAGE,
                    },
                ]);
                return;
            }
            const latestActiveCardUsers = await walletRepository_1.WalletRepository.getUsersListForActiveTeam({
                teamId: teamID,
                userId: user?.userId,
            });
            const hasActiveCard = await fetchCardInfo(latestActiveCardUsers);
            const latestDecoratedUser = await teamRepository_1.TeamRepository.getDecoratedUser({ teamId: teamID, userId: user?.userId });
            setPageLoading(false);
            const conditions = [
                { check: hasActiveCard, message: strings_1.userDetailsStrings.ACTIVE_CARD_USERS_ERROR },
                {
                    check: latestDecoratedUser?.hasOpenRequests,
                    message: isHsbcCardProvider ? removeUserHSBCWarning() : strings_1.userDetailsStrings.PENDING_REQUEST_ERROR,
                },
                {
                    check: latestDecoratedUser?.isLastAdmin,
                    message: isHsbcCardProvider ? removeUserHSBCWarning() : strings_1.userDetailsStrings.NO_TEAM_OWNER_AND_MANAGER_ERROR,
                },
                {
                    check: latestDecoratedUser?.requesters && latestDecoratedUser?.requesters.length > 0,
                    message: strings_1.userDetailsStrings.USER_REQUESTERS_ERROR({ firstName: latestDecoratedUser?.firstName }),
                },
            ];
            const errorCondition = conditions.find(({ check }) => check);
            if (errorCondition) {
                setDialog({
                    type: strings_1.notificationStrings.WARNING,
                    message: errorCondition.message,
                    open: true,
                    actionText: 'OK',
                    showCancel: false,
                    onAccept: () => ({}),
                });
                return;
            }
            setDialog({
                message: strings_1.userDetailsStrings.REMOVE_USER_WARNING({ firstName: user.firstName }),
                type: strings_1.notificationStrings.WARNING,
                open: true,
                actionText: 'OK',
                showCancel: true,
                onAccept: handleAlert,
            });
        }
        catch (error) {
            logger.error({ message: 'Error removing user', error });
            setNotification([
                {
                    type: strings_1.notificationStrings.ERROR,
                    message: strings_1.peopleTableStrings.USER_REMOVE_ERROR_MESSAGE,
                },
            ]);
            setPageLoading(false);
        }
    };
    const footerButtons = ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [isOooAvailable ? ((0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "outOfOffice-button", onClick: () => ({}), color: uiConstants_1.UI_BUTTON_SECONDARY, style: { width: '100%' }, label: strings_1.sessionUserDetailsStrings.OUT_OF_OFFICE_BUTTON_TEXT })) : null, (0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "save-button", onClick: () => onFormSubmit({ teamUsers: usersData }), color: uiConstants_1.UI_BUTTON_PRIMARY, style: { width: '100%' }, disabled: isUserDetailSaveBtnDisabled, label: strings_1.sessionUserDetailsStrings.SAVE_BUTTON_TEXT }), (0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "removeFromTeam-button", onClick: removeUser, color: "warning", variant: "outlined", style: {
                    width: '100%',
                    ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                        ? {
                            borderRadius: 0,
                        }
                        : {}),
                }, label: strings_1.sessionUserDetailsStrings.REMOVE_FROM_TEAM_TEXT }), (0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "cancel-button", onClick: () => {
                    setRequestLimit(currentUserDetailsAside?.teams?.[teamID]?.requestLimit);
                    setIsUserDetailsAsideOpen(false);
                }, color: uiConstants_1.UI_BUTTON_SECONDARY, style: { width: '100%' }, label: strings_1.sessionUserDetailsStrings.CANCEL_BUTTON_TEXT })] }));
    return ((0, jsx_runtime_1.jsx)(drawerAside_1.default, { id: 'userDetailsAside', title: lexico.tr('User Details'), open: isUserDetailsAsideOpen, onClose: () => {
            setRequestLimit(currentUserDetailsAside?.teams?.[teamID]?.requestLimit);
            setIsUserDetailsAsideOpen(false);
        }, footerButtons: footerButtons, children: (0, jsx_runtime_1.jsx)(material_1.FormGroup, { style: { margin: 1, justifyContent: 'space-between', flex: 1 }, children: (0, jsx_runtime_1.jsxs)(material_1.Grid, { container: true, rowSpacing: 1, direction: "row", sx: {
                    margin: 0,
                }, children: [(0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsx)(material_1.Avatar, { alt: user?.fullName, "ts-variant": "profile", src: user.avatarUrl ? (0, avatarUtils_1.getSquare)(user?.avatarUrl, 145) : user?.fullName || '' }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: "Name" }), (0, jsx_runtime_1.jsx)(material_1.OutlinedInput, { inputProps: {
                                        'data-testid': 'fullName-input',
                                    }, id: 'fullName', name: 'fullName', required: true, type: 'text', value: user?.fullName, readOnly: true })] }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { children: strings_1.inputStrings.EMAIL }), (0, jsx_runtime_1.jsx)(material_1.OutlinedInput, { "data-testid": 'email-input', inputProps: {
                                        'data-testid': 'email-input',
                                    }, id: 'email-input', name: 'email-input', type: 'email', value: user?.email, readOnly: true, title: user?.email })] }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: isHsbcCardProvider ? ((0, jsx_runtime_1.jsxs)(material_1.FormControl, { margin: "normal", children: [(0, jsx_runtime_1.jsx)(material_1.InputLabel, { id: "phoneNumber", children: "Phone Number *" }), (0, jsx_runtime_1.jsx)(StyledMuiTelInput_1.StyledMuiTelInput, { sx: {
                                        padding: 0,
                                    }, slotProps: {
                                        input: {
                                            name: 'phoneNumber',
                                            required: true,
                                            autoFocus: false,
                                        },
                                    }, defaultCountry: "GB", onlyCountries: addPeopleAside_1.onlyCountriesCodePhoneNumbers, forceCallingCode: true, focusOnSelectCountry: true, MenuProps: { disableAutoFocusItem: true }, value: `${user?.phoneDetails?.phoneCountryCode || 'Missing Country Code'} ${user?.phoneDetails?.phoneNumber || 'Missing Phone Number'}` })] })) : null }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { htmlFor: "roleId", children: strings_1.inputStrings.ROLE }), (0, jsx_runtime_1.jsx)(material_1.Select, { name: 'roleId', inputProps: { placeholder: strings_1.inputStrings.ROLE_PLACEHOLDER }, value: selectedRole?.id || currentUserRole?.id || '', onChange: changeUserData, children: roles && Object.keys(roles).length > 0
                                        ? Object.keys(roles)?.map(roleKey => ((0, jsx_runtime_1.jsx)(material_1.MenuItem, { value: roles[roleKey]?.id, children: roles[roleKey]?.label }, roleKey)))
                                        : [] })] }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsxs)(material_1.FormControl, { sx: { pt: 1 }, children: [(0, jsx_runtime_1.jsx)(FormLabel_1.StyledFormLabel, { htmlFor: "userId", children: strings_1.inputStrings.APPROVER }), (0, jsx_runtime_1.jsx)(material_1.Select, { name: 'userId', displayEmpty: true, inputProps: { placeholder: strings_1.inputStrings.APPROVER_PLACEHOLDER }, value: approver?.userId || '', onChange: changeApproverData, required: true, children: teamApprovers?.map(obj => ((0, jsx_runtime_1.jsx)(material_1.MenuItem, { value: obj?.userId, children: obj?.fullName }, obj?.userId))) })] }) }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: hasRequestLimit ? ((0, jsx_runtime_1.jsx)(requestLimitInput_1.default, { value: requestLimit, onChange: event => setRequestLimit(event.target.value) })) : ('') })] }) }) }));
};
exports.default = UserDetailsAside;
